import { useState, useEffect, useCallback, useRef } from "react";
import AdminLayout from "../components/AdminLayout";
import {
  Card, Button, Modal, Input, Textarea, EmptyState, SearchBar, Pagination,
  FilterBar, PageHeader, ListSkeleton, IconButton, Avatar, Chip, StickyActions,
  ListPage, ListSurface, stickyHeadCls, actionColCls, actionHeadCls,
  listTableCls, listCardsCls, thCls, tdCls, actionW, cx,
} from "../components/UI";
import api from "../utils/api";
import toast from "react-hot-toast";
import { Plus, Pencil, Trash2, Mail, Phone, MapPin, Building2 } from "lucide-react";

const LIMIT = 15;
const INIT = { name: "", email: "", phone: "", company: "", address: "", city: "", state: "", country: "India", gstin: "", notes: "" };

const location = (c) => [c.city, c.state, c.country].filter(Boolean).join(", ");

/* Mobile row — same information as the table, stacked for a thumb */
function ClientCard({ c, onEdit, onDelete }) {
  return (
    <Card className="p-4" hover>
      {/* actions lead the card, mirroring the table's first column */}
      <div className="flex items-center gap-1.5 pb-3 mb-3 border-b border-ink-100">
        <Button size="xs" variant="outline" icon={<Pencil size={13} />} onClick={() => onEdit(c)}>Edit</Button>
        <Button size="xs" variant="dangerSoft" icon={<Trash2 size={13} />} onClick={() => onDelete(c._id)}>Delete</Button>
      </div>

      <div className="flex items-start gap-3">
        <Avatar name={c.name} tone="violet" size="md" />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-ink-900 truncate">{c.name}</div>
          {c.company && <div className="text-xs text-ink-400 truncate mt-0.5">{c.company}</div>}
        </div>
      </div>

      <div className="mt-3 space-y-1.5 text-xs">
        {c.email && (
          <a href={`mailto:${c.email}`} className="flex items-center gap-2 text-ink-600 hover:text-brand-600">
            <Mail size={13} className="text-ink-400 shrink-0" /><span className="truncate">{c.email}</span>
          </a>
        )}
        {c.phone && (
          <a href={`tel:${c.phone}`} className="flex items-center gap-2 text-ink-600 hover:text-brand-600">
            <Phone size={13} className="text-ink-400 shrink-0" />{c.phone}
          </a>
        )}
        {location(c) && (
          <div className="flex items-center gap-2 text-ink-500">
            <MapPin size={13} className="text-ink-400 shrink-0" /><span className="truncate">{location(c)}</span>
          </div>
        )}
      </div>

      {c.gstin && <div className="mt-3 pt-3 border-t border-ink-100"><Chip tone="slate">GSTIN {c.gstin}</Chip></div>}
    </Card>
  );
}

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(INIT);
  const [saving, setSaving] = useState(false);
  const debRef = useRef(null);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ page, limit: LIMIT });
      if (search) p.set("search", search);
      const { data } = await api.get(`/clients?${p}`);
      setClients(data?.data?.clients || []);
      setTotal(data.data.total);
      setPages(data.data.pages);
    } catch { toast.error("Failed to load clients"); }
    finally { setLoading(false); }
  }, [page, search]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const handleSearch = (v) => {
    clearTimeout(debRef.current);
    debRef.current = setTimeout(() => { setSearch(v); setPage(1); }, 350);
    setSearch(v);
  };

  const openCreate = () => { setForm(INIT); setSelected(null); setModal("create"); };
  const openEdit = (c) => {
    setSelected(c);
    setForm({
      name: c.name, email: c.email || "", phone: c.phone || "", company: c.company || "",
      address: c.address || "", city: c.city || "", state: c.state || "", country: c.country || "India",
      gstin: c.gstin || "", notes: c.notes || "",
    });
    setModal("edit");
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (modal === "create") { await api.post("/clients", form); toast.success("Client created!"); }
      else { await api.put(`/clients/${selected._id}`, form); toast.success("Client updated!"); }
      setModal(null); fetchClients();
    } catch (err) { toast.error(err.response?.data?.message || "Failed"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this client?")) return;
    try { await api.delete(`/clients/${id}`); toast.success("Client deleted"); fetchClients(); }
    catch { toast.error("Failed"); }
  };

  const f = (k) => ({ value: form[k], onChange: (e) => setForm({ ...form, [k]: e.target.value }) });

  return (
    <AdminLayout fullHeight>
      <ListPage>
        <PageHeader
          title="Clients"
          subtitle={`${total} client${total !== 1 ? "s" : ""} on record`}
          action={<Button onClick={openCreate} icon={<Plus size={16} />} className="w-full sm:w-auto">Add Client</Button>}
        />

        <FilterBar className="shrink-0">
          <SearchBar value={search} onChange={handleSearch} placeholder="Search name, email, company, city…" className="flex-1 min-w-[180px]" />
          {search && (
            <button onClick={() => { setSearch(""); setPage(1); }} className="text-xs font-semibold text-brand-600 hover:text-brand-700 px-1">
              Clear
            </button>
          )}
        </FilterBar>

        {loading ? (
          <div className="flex-1 min-h-0 overflow-hidden"><ListSkeleton rows={6} cols={6} /></div>
        ) : clients.length === 0 ? (
          <Card className="flex-1 min-h-0 grid place-items-center">
            <EmptyState icon={<Building2 size={22} />} title="No clients found"
              description={search ? "Try a different search term." : "Add your first client to start billing."}
              action={!search && <Button onClick={openCreate} icon={<Plus size={16} />}>Add Client</Button>} />
          </Card>
        ) : (
          <ListSurface footer={<Pagination page={page} pages={pages} total={total} limit={LIMIT} onPage={setPage} />}>
            {/* ── Desktop table ── */}
            <table className={listTableCls}>
              <thead className={stickyHeadCls}>
                <tr>
                  <th className={cx(thCls, actionHeadCls, actionW(2))}>Actions</th>
                  <th className={thCls}>Client</th>
                  <th className={thCls}>Contact</th>
                  <th className={thCls}>Company</th>
                  <th className={thCls}>Location</th>
                  <th className={cx(thCls, "w-[170px]")}>GSTIN</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {clients.map(c => (
                  <tr key={c._id} className="group hover:bg-ink-50 transition-colors">
                    <td className={cx(tdCls, actionColCls, "px-3")}>
                      <div className="flex gap-0.5">
                        <IconButton size="sm" variant="outline" icon={<Pencil size={15} />} onClick={() => openEdit(c)} title="Edit" />
                        <IconButton size="sm" variant="danger" icon={<Trash2 size={15} />} onClick={() => handleDelete(c._id)} title="Delete" />
                      </div>
                    </td>
                    <td className={tdCls}>
                      <div className="flex items-center gap-3 min-w-0">
                        <Avatar name={c.name} tone="violet" size="sm" />
                        <span className="font-semibold text-ink-800 truncate">{c.name}</span>
                      </div>
                    </td>
                    <td className={tdCls}>
                      {c.email && <a href={`mailto:${c.email}`} className="block text-xs text-ink-600 hover:text-brand-600 truncate">{c.email}</a>}
                      {c.phone && <a href={`tel:${c.phone}`} className="block text-xs text-ink-400 hover:text-brand-600 truncate">{c.phone}</a>}
                      {!c.email && !c.phone && <span className="text-ink-300">—</span>}
                    </td>
                    <td className={cx(tdCls, "text-xs text-ink-600")}>
                      <div className="truncate">{c.company || <span className="text-ink-300">—</span>}</div>
                    </td>
                    <td className={cx(tdCls, "text-xs text-ink-500")}>
                      <div className="truncate">{location(c) || <span className="text-ink-300">—</span>}</div>
                    </td>
                    <td className={tdCls}>
                      {c.gstin
                        ? <span className="block font-mono text-[11px] bg-ink-100 px-2 py-1 rounded-md text-ink-600 truncate">{c.gstin}</span>
                        : <span className="text-ink-300">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* ── Mobile / tablet cards ── */}
            <div className={listCardsCls}>
              {clients.map(c => <ClientCard key={c._id} c={c} onEdit={openEdit} onDelete={handleDelete} />)}
            </div>
          </ListSurface>
        )}
      </ListPage>

      <Modal
        open={modal === "create" || modal === "edit"}
        onClose={() => setModal(null)}
        title={modal === "create" ? "Add Client" : "Edit Client"}
        subtitle={modal === "edit" ? selected?.name : "New client record"}
        icon={<Building2 size={18} />}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Client Name" required {...f("name")} placeholder="Contact / organisation name" />
            <Input label="Company" {...f("company")} placeholder="Company Ltd." />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Email" type="email" {...f("email")} placeholder="name@company.com" icon={<Mail size={15} />} />
            <Input label="Phone" type="tel" {...f("phone")} placeholder="+91 ..." icon={<Phone size={15} />} />
          </div>
          <Input label="Address" {...f("address")} placeholder="Street address" />
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <Input label="City" {...f("city")} />
            <Input label="State" {...f("state")} />
            <Input label="Country" {...f("country")} wrapClass="col-span-2 sm:col-span-1" />
          </div>
          <Input label="GSTIN" {...f("gstin")} placeholder="22AAAAA0000A1Z5" className="font-mono" />
          <Textarea label="Notes" {...f("notes")} placeholder="Anything worth remembering about this client…" />

          <StickyActions>
            <Button type="button" variant="secondary" onClick={() => setModal(null)} block className="sm:w-auto">Cancel</Button>
            <Button type="submit" loading={saving} block className="sm:w-auto">
              {modal === "create" ? "Add Client" : "Save Changes"}
            </Button>
          </StickyActions>
        </form>
      </Modal>
    </AdminLayout>
  );
}
